import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import {
  getListPeriode,
  getListProgramStudiPerPeriode,
} from "@/services/getData";
import { DataSiswa, Periode } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { MdInput } from "react-icons/md";

const insertPendaftaran = async ({
  id_data_siswa,
  id_sys_periode,
  uk_id,
  custom_waktu_selesai_daftar_valid,
}: {
  id_data_siswa: number | undefined;
  id_sys_periode: string;
  uk_id: string;
  custom_waktu_selesai_daftar_valid: string | null;
}) => {
  const response = await api.post("/data-siswa/tambah-pendaftaran-pasca", {
    id_data_siswa,
    id_sys_periode,
    uk_id,
    custom_waktu_selesai_daftar_valid,
  });
  return response.data;
};

export default function FormInsertPendaftaran({
  selectedRow,
  closeModal,
}: {
  selectedRow: DataSiswa | null;
  closeModal: () => void;
}) {
  const [openPeriode, setOpenPeriode] = useState(false);
  const [openListProdi, setOpenListProdi] = useState(false);
  const [form, setForm] = useState({
    id_sys_periode: "",
    uk_id: "",
    custom_waktu_selesai_daftar_valid: "",
  });

  const { data: dataPeriode, isPending: isPendingDataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });

  const { data: dataProdiPasca, isFetching: isFetchingProdiPasca } = useQuery({
    queryKey: ["listProdiPasca", form.id_sys_periode],
    queryFn: () => getListProgramStudiPerPeriode(form.id_sys_periode),
    select: (data) => {
      return data?.map((item: any) => {
        return { label: item?.unitkerja?.nama, value: item?.uk_id };
      });
    },
  });

  const filteredPeriode =
    dataPeriode &&
    dataPeriode.filter((periode: Periode) => {
      return periode.jalur_jenis?.nama_jalur_jenis_slug === "pascasarjana";
    });

  const optionsPeriode =
    filteredPeriode?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: insertPendaftaran,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataSiswa"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }
    mutate({
      id_data_siswa: selectedRow?.id_data_siswa,
      custom_waktu_selesai_daftar_valid: form.custom_waktu_selesai_daftar_valid
        ? new Date(form.custom_waktu_selesai_daftar_valid)
            .toLocaleString("sv-SE", { hour12: false })
            .replace("T", " ")
        : null,
      id_sys_periode: form.id_sys_periode,
      uk_id: form.uk_id,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader title="Insert Pendaftaran Pasca" icon={<MdInput />} />
      <div>
        <Label>Periode</Label>
        <Combobox
          isPending={isPendingDataPeriode}
          open={openPeriode}
          setOpen={setOpenPeriode}
          value={form.id_sys_periode}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, id_sys_periode: value }));
          }}
          options={optionsPeriode}
          placeholder="Pilih Periode"
        />
      </div>
      {!isFetchingProdiPasca && form.id_sys_periode ? (
        <div>
          <Label>Prodi</Label>
          <Combobox
            open={openListProdi}
            setOpen={setOpenListProdi}
            value={form.uk_id || ""}
            setValue={(value) => {
              setForm((prev) => ({ ...prev, uk_id: value }));
            }}
            options={dataProdiPasca}
            placeholder="Pilih prodi"
          />
        </div>
      ) : (
        "Memuat"
      )}
      <div>
        <Label>Waktu Selesai Validasi Pendaftaran</Label>
        <Input
          type="datetime-local"
          value={form.custom_waktu_selesai_daftar_valid}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              custom_waktu_selesai_daftar_valid: event.target.value,
            }))
          }
        />
      </div>

      <div className="mt-3 flex gap-3">
        <Button
          variant={"outline"}
          type="button"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  );
}
